import { pickWorkspaceId } from '../workspace/workspaceId';

export type GenericRecord = Record<string, any>;

export interface WorkspaceSummaryRecord extends GenericRecord {
  id?: string | number;
  pk?: string | number;
  workspace_id?: string | number;
  uuid?: string | number;
}

const asRecord = (value: unknown): GenericRecord | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as GenericRecord;
};

const collectWorkspaceIds = (workspaces: unknown): string[] => {
  if (!Array.isArray(workspaces)) return [];

  return workspaces
    .map((workspace) => {
      const record = asRecord(workspace) as WorkspaceSummaryRecord | null;
      if (!record) return null;
      return pickWorkspaceId(
        record.id,
        record.pk,
        record.workspace_id,
        record.uuid
      );
    })
    .filter(Boolean) as string[];
};

export const deriveWorkspaceIdFromSummary = (
  summary: unknown
): string | null => {
  const record = asRecord(summary);
  if (!record) return null;

  const workspaceContext =
    asRecord(record.workspace_context) ||
    asRecord(record.workspaceContext) ||
    asRecord(asRecord(record.data)?.workspace_context) ||
    asRecord(asRecord(record.data)?.workspaceContext);

  const workspaces =
    record.workspaces ||
    asRecord(record.data)?.workspaces ||
    workspaceContext?.workspaces ||
    null;

  const availableIds = collectWorkspaceIds(workspaces);
  const preferredId = pickWorkspaceId(
    workspaceContext?.active_workspace_id,
    workspaceContext?.activeWorkspaceId
  );

  if (
    preferredId &&
    (availableIds.length === 0 || availableIds.includes(preferredId))
  ) {
    return preferredId;
  }

  if (availableIds.length > 0) {
    return availableIds[0];
  }

  return null;
};

export const deriveWorkspaceIdFromUser = (user: unknown): string | null => {
  const record = asRecord(user);
  if (!record) return null;

  return pickWorkspaceId(
    record.active_workspace_id,
    record.default_workspace_id,
    record.active_workspace,
    record.active_seed_id,
    record.default_seed_id
  );
};
